<template>
  <div class="h-full p-4">
    <ul class="menu bg-base-200 rounded-box w-full">
      <!-- ===== admin(gnbs) 메뉴 ===== -->
      <template v-for="section in adminMenu" :key="'adm-' + section.id">
        <!-- 하위 메뉴가 있는 그룹 (접이식 토글) -->
        <li v-if="section.children && section.children.length">
          <details class="group" :open="adminGroupActive(section)">
            <summary class="list-none [&::-webkit-details-marker]:hidden">
              <span v-if="adminIcon(section.id)" class="mr-1">{{ adminIcon(section.id) }}</span>
              {{ section.title }}
              <span v-if="section.id === 'inquiry' && franchiseNew > 0" class="ml-1 inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-extrabold leading-none text-white bg-red-600 shadow-sm ring-1 ring-red-700 animate-pulse">NEW</span>
              <span v-else-if="groupHasNew(section)" class="ml-1 inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-extrabold leading-none text-white bg-red-600 shadow-sm ring-1 ring-red-700">NEW</span>
              <svg
                class="ml-auto w-4 h-4 shrink-0 text-gray-400 transition-transform group-open:rotate-90"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                stroke-width="2"
              >
                <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </summary>
            <ul>
              <li v-for="item in section.children" :key="item.id">
                <a @click="openAdmin(item.route)" class="flex items-center gap-2 cursor-pointer" :class="{ active: isAdminActive(item.route) }">
                  <span>{{ item.title }}</span>
                  <span v-if="item.route && boardNewKeys.has(item.route)" class="inline-flex items-center rounded px-1 py-0.5 text-[9px] font-extrabold leading-none text-white bg-red-600">NEW</span>
                </a>
              </li>
            </ul>
          </details>
        </li>
        <!-- 단일 항목 (토글 없음) — 그룹 summary 와 아이콘/텍스트 정렬을 동일하게 맞춘다 -->
        <li v-else>
          <a @click="openAdmin(section.route)" class="cursor-pointer flex items-center gap-1" :class="{ active: isAdminActive(section.route) }">
            <span v-if="adminIcon(section.id)" class="mr-1">{{ adminIcon(section.id) }}</span>
            <span>{{ section.title }}</span>
            <span v-if="section.route && boardNewKeys.has(section.route)" class="inline-flex items-center rounded px-1 py-0.5 text-[9px] font-extrabold leading-none text-white bg-red-600">NEW</span>
          </a>
        </li>
      </template>

      <!-- ===== LMS 학습관리 메뉴 ===== -->
      <li v-for="section in visibleMenu" :key="section.id">
        <details class="group" :open="(section.children ?? []).some((c) => isLmsActive(c.path))">
          <summary class="list-none [&::-webkit-details-marker]:hidden">
            <!-- 본사관리 섹션은 빌딩 아이콘으로 구분 -->
            <svg
              v-if="section.id === 'hq'"
              xmlns="http://www.w3.org/2000/svg"
              class="w-4 h-4 mr-2 text-indigo-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0H5m14 0h2M5 21H3m4-14h.01M11 7h.01M15 7h.01M7 11h.01M11 11h.01M15 11h.01M7 15h.01M11 15h.01M15 15h.01"
              />
            </svg>
            <svg
              v-else
              xmlns="http://www.w3.org/2000/svg"
              class="w-4 h-4 mr-2 text-yellow-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
              />
            </svg>
            {{ section.title }}
            <svg
              class="ml-auto w-4 h-4 shrink-0 text-gray-400 transition-transform group-open:rotate-90"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              stroke-width="2"
            >
              <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </summary>
          <ul>
            <li v-for="item in section.children" :key="item.id">
              <a
                v-if="item.isExternal"
                :href="item.path"
                target="_blank"
                rel="noopener noreferrer"
                class="flex items-center gap-2"
              >
                {{ item.title }}
              </a>
              <a v-else @click="handleMenuClick(item.path)" class="flex items-center gap-2" :class="{ active: isLmsActive(item.path) }">
                <svg v-if="item.id === 'learning-history'" xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="none">
                  <path d="M4 19.5A2.5 2.5 0 016.5 17H20" stroke="#3b82f6" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z" stroke="#3b82f6" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M8 7h8M8 11h5" stroke="#60a5fa" stroke-width="1.5" stroke-linecap="round"/>
                </svg>
                <svg v-else-if="item.id === 'assignment-status'" xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="none">
                  <rect x="3" y="3" width="18" height="18" rx="3" stroke="#10b981" stroke-width="2"/>
                  <path d="M8 12l2.5 2.5L16 9" stroke="#34d399" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <svg v-else-if="item.id === 'student-history'" xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="8" r="4" stroke="#8b5cf6" stroke-width="2"/>
                  <path d="M5 20c0-3.314 3.134-6 7-6s7 2.686 7 6" stroke="#a78bfa" stroke-width="2" stroke-linecap="round"/>
                </svg>
                <svg v-else-if="item.id === 'ranking'" xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="none">
                  <path d="M6 9H2l2.5-5h3L6 9z" fill="#fbbf24"/>
                  <path d="M18 9h4l-2.5-5h-3L18 9z" fill="#fbbf24"/>
                  <path d="M12 2l2.4 4.8L20 7.5l-4 3.9.9 5.6L12 14.5 7.1 17l.9-5.6-4-3.9 5.6-.7L12 2z" fill="#f59e0b" stroke="#d97706" stroke-width="0.5"/>
                </svg>
                <svg v-else-if="item.id === 'ranking-personal'" xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="9" r="6" stroke="#f97316" stroke-width="2"/>
                  <circle cx="12" cy="9" r="3.5" fill="#fdba74"/>
                  <path d="M9 15l-2 7 5-3 5 3-2-7" stroke="#f97316" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <svg v-else-if="item.id === 'learning-statistics'" xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="none">
                  <rect x="3" y="13" width="4" height="8" rx="1" fill="#06b6d4"/>
                  <rect x="10" y="8" width="4" height="13" rx="1" fill="#0891b2"/>
                  <rect x="17" y="3" width="4" height="18" rx="1" fill="#0e7490"/>
                </svg>
                <svg v-else-if="item.id === 'achievement-management'" xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="none">
                  <path d="M12 2L9 9H2l5.5 4.5L5 21l7-4.5L19 21l-2.5-7.5L22 9h-7L12 2z" fill="#ec4899" stroke="#db2777" stroke-width="0.5"/>
                  <circle cx="12" cy="11" r="3" fill="#f9a8d4"/>
                </svg>
                <svg v-else-if="item.id === 'member-management'" xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="none">
                  <circle cx="9" cy="7" r="4" stroke="#0d9488" stroke-width="2"/>
                  <path d="M2 21c0-3.314 3.134-6 7-6s7 2.686 7 6" stroke="#0d9488" stroke-width="2" stroke-linecap="round"/>
                  <path d="M19 8v6M16 11h6" stroke="#14b8a6" stroke-width="2" stroke-linecap="round"/>
                </svg>
                <svg v-else-if="item.id === 'reengkigo-setting'" xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="none">
                  <path d="M12 15.5A3.5 3.5 0 1012 8.5a3.5 3.5 0 000 7z" stroke="#6366f1" stroke-width="2"/>
                  <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 11-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 11-4 0v-.09a1.65 1.65 0 00-1.08-1.51 1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 11-2.83-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 110-4h.09a1.65 1.65 0 001.51-1.08 1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 112.83-2.83l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 114 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 112.83 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9c.26.604.852.997 1.51 1H21a2 2 0 110 4h-.09a1.65 1.65 0 00-1.51 1z" stroke="#818cf8" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <svg v-else-if="item.id === 'online-users'" xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="9" stroke="#22c55e" stroke-width="2"/>
                  <circle cx="12" cy="12" r="4" fill="#4ade80"/>
                </svg>
                <svg v-else-if="item.id === 'book-table'" xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="none">
                  <path d="M4 4h6a2 2 0 012 2v14a2 2 0 00-2-2H4V4z" stroke="#2563eb" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M20 4h-6a2 2 0 00-2 2v14a2 2 0 012-2h6V4z" stroke="#3b82f6" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <svg v-else-if="item.id === 'product-kind-weight'" xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="none">
                  <path d="M12 3v18M7 21h10M12 6l-7 2 3 5a3 3 0 01-6 0l3-5M12 6l7 2-3 5a3 3 0 006 0l-3-5" stroke="#0891b2" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <svg v-else-if="item.id === 'rate-group'" xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="none">
                  <path d="M4 20V10M10 20V4M16 20v-7M22 20H2" stroke="#0891b2" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <svg v-else-if="item.id === 'achievement-admin'" xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="none">
                  <path d="M8 21h8M12 17v4M7 4h10v4a5 5 0 01-10 0V4z" stroke="#f59e0b" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M17 5h3v2a3 3 0 01-3 3M7 5H4v2a3 3 0 003 3" stroke="#fbbf24" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <svg v-else-if="item.id === 'quokkaroom-admin'" xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="none">
                  <path d="M3 10.5L12 3l9 7.5" stroke="#0d9488" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M5 9.5V20h14V9.5" stroke="#14b8a6" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  <rect x="10" y="13" width="4" height="7" fill="#5eead4"/>
                </svg>
                <svg v-else-if="item.id === 'alimtalk-templates'" xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="none">
                  <path d="M4 5a2 2 0 012-2h12a2 2 0 012 2v9a2 2 0 01-2 2H9l-5 4V5z" stroke="#d97706" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M8 8h8M8 11h5" stroke="#fbbf24" stroke-width="1.5" stroke-linecap="round"/>
                </svg>
                <svg v-else-if="item.id === 'alimtalk-test'" xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="none">
                  <path d="M4 5a2 2 0 012-2h12a2 2 0 012 2v9a2 2 0 01-2 2H9l-5 4V5z" stroke="#ea580c" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M8.5 9.5l2 2 3.5-3.5" stroke="#fb923c" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                {{ item.title }}
              </a>
            </li>
          </ul>
        </details>
      </li>

      <!-- ===== 바로가기 (ReengKi 패밀리 서비스 · 외부 링크) ===== -->
      <li>
        <details class="group">
          <summary class="list-none [&::-webkit-details-marker]:hidden">
            <span class="mr-1">🔗</span>
            바로가기
            <svg
              class="ml-auto w-4 h-4 shrink-0 text-gray-400 transition-transform group-open:rotate-90"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              stroke-width="2"
            >
              <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </summary>
          <ul>
            <li v-for="sc in shortcuts" :key="sc.url">
              <a :href="sc.url" target="_blank" rel="noopener noreferrer" class="flex items-center gap-2">
                <span
                  class="inline-flex w-4 h-4 shrink-0 items-center justify-center rounded text-[9px] font-extrabold leading-none text-white"
                  :style="{ backgroundColor: sc.color }"
                >
                  {{ sc.badge }}
                </span>
                <span class="flex-1 min-w-0 truncate">{{ sc.name }}</span>
                <span class="ml-auto text-gray-400 text-xs">↗</span>
              </a>
            </li>
          </ul>
        </details>
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed } from "vue";
import { useRouter, useRoute } from "vue-router";
import menuItems from "../data/menu";
import shortcuts from "../data/shortcuts";
import type { MenuItem } from "../types/menu";
import { buildAdminMenu, migratedPath, adminIcon } from "../data/adminMenu";
import { getAdminAuth } from "../utils/adminAuth";
import { lmsApi } from "../utils/api";

const router = useRouter();
const route = useRoute();
const accountType = ref("");

// 현재 열려있는 화면 강조: 경로가 정확히 일치하거나 하위(상세/글쓰기 등)이면 활성으로 본다.
function pathMatches(target?: string | null): boolean {
  if (!target) return false;
  const cur = route.path;
  return cur === target || cur.startsWith(target + "/");
}
// admin(gnbs) 메뉴: route → 내부경로(migratedPath) 로 변환 후 비교
function isAdminActive(itemRoute?: string): boolean {
  return pathMatches(migratedPath(itemRoute));
}
function adminGroupActive(section: { route?: string; children?: { route?: string }[] }): boolean {
  if (section.route) return isAdminActive(section.route);
  return (section.children ?? []).some((c) => isAdminActive(c.route));
}
// LMS 학습관리 메뉴: item.path 직접 비교
function isLmsActive(path?: string): boolean {
  return pathMatches(path);
}

// 미상담 가맹문의 NEW 배지 — 지사 계정에서 자기 지사의 미상담 건이 있으면 표시(지속 알림).
const franchiseNew = ref(0);
const isAgencyAcct = computed(() => Number(getAdminAuth()?.AccountTypeID) === 2);
async function loadFranchiseNew() {
  if (!isAgencyAcct.value) return;
  try {
    const res = await lmsApi.get<any>("/api/admin/franchise-inquiries/uncontacted-count");
    franchiseNew.value = Number(res?.data?.count ?? 0);
  } catch (e) {
    console.error("미상담 가맹문의 건수 로드 실패:", e);
  }
}
let franchiseNewTimer: ReturnType<typeof setInterval> | undefined;

// ── 게시판 New(안 읽음) 배지 ──────────────────────────────────────────
// 게시판으로 취급하는 라우트(백엔드 board-updates 와 대칭). material-* 는 동적 자료실.
const BOARD_ROUTES = new Set([
  "notice", "agency-notice", "share-board-agen", "share-board-aca",
  "qna-FAQ", "qna-proposal", "agency-proposal-board", "agency-active-share", "materials",
]);
function isBoardRoute(route?: string): boolean {
  return !!route && (BOARD_ROUTES.has(route) || route.startsWith("material-"));
}
const boardNewKeys = ref<Set<string>>(new Set());
function groupHasNew(section: { children?: { route?: string }[] }): boolean {
  return (section.children ?? []).some((c) => c.route && boardNewKeys.value.has(c.route));
}
// 현재 메뉴에 노출된 게시판 라우트 수집
function collectBoardRoutes(): string[] {
  const out: string[] = [];
  for (const section of adminMenu.value) {
    if (isBoardRoute(section.route)) out.push(section.route as string);
    for (const child of section.children ?? []) if (isBoardRoute(child.route)) out.push(child.route as string);
  }
  return [...new Set(out)];
}
async function loadBoardUpdates() {
  if (!getAdminAuth()?.gnbs) return;
  const keys = collectBoardRoutes();
  if (!keys.length) { boardNewKeys.value = new Set(); return; }
  try {
    const res = await lmsApi.post<any>("/api/admin/board-updates", { keys });
    boardNewKeys.value = new Set((res?.newKeys ?? []).map(String));
  } catch (e) {
    console.error("게시판 New 배지 로드 실패:", e); // 테이블 미적용 등 — 배지만 비활성, 메뉴는 정상
  }
}
// 게시판 열람(BoardList)에서 읽음 처리 후 발행하는 이벤트 → 배지 갱신
function onBoardRead() { loadBoardUpdates(); }

// 자료실 게시판 route→이름 (menus 테이블, 실시간). 로그인 캐시(gnbs)가 낡아도 라벨을 보정.
const materialNames = ref<Record<string, string>>({});

// admin(gnbs) 메뉴 — admin 계정으로 로그인한 경우 표시
const adminMenu = computed(() => {
  const menu = buildAdminMenu(getAdminAuth()?.gnbs as any);
  const names = materialNames.value;
  if (Object.keys(names).length) {
    for (const section of menu) {
      if (section.id !== "material" || !section.children) continue;
      for (const child of section.children) {
        const name = child.route ? names[child.route] : undefined;
        if (name) child.title = name;
      }
    }
  }
  // 행사 섹션 재구성(결정적):
  //  - 전체현황/교육/사설/학설/기타를 종류 필터가 있는 "행사관리"(event-manage) 한 화면으로 통합
  //  - 본사(root)만 "본사 미팅관리"(event-zoom, LMS 네이티브) 추가
  //  - 원장(3)만 "내 줌미팅 참여"(event-my-zoom) 추가
  //  gnbs 가 그룹(children)이든 단일항목으로 접혔든, 캐시된 낡은 gnbs 든 동일 결과로 덮어쓴다.
  const evIdx = menu.findIndex((s) => s.id === "event" || (s.route && s.route.startsWith("event-")));
  if (evIdx >= 0) {
    const at = Number(getAdminAuth()?.AccountTypeID);
    const children = [{ id: "event-manage", title: "행사관리", route: "event-manage" }];
    if (at === 1) {
      children.push({ id: "event-zoom", title: "본사 미팅관리", route: "event-zoom" });
    }
    if (at === 2) {
      children.push({ id: "event-zoom-agency", title: "미팅관리", route: "event-zoom-agency" });
    }
    if (at === 3) {
      children.push({ id: "event-my-zoom", title: "참여 내역", route: "event-my-zoom" });
    }
    menu[evIdx] =
      children.length === 1
        ? { id: "event", title: "행사관리", route: "event-manage" } // 단일 → 토글 없는 항목
        : { id: "event", title: "행사관리", children }; // 그룹(행사관리 / 본사 미팅관리 or 내 줌미팅 참여)
  }
  return menu;
});

// 모든 admin 화면은 LMS 내부로 이관 완료 — route 를 내부 경로로 변환해 라우팅한다.
function openAdmin(route?: string) {
  if (!route) return;
  const internal = migratedPath(route);
  if (internal) {
    router.push(internal);
    emit("menuClick");
  }
}

// 메뉴 클릭 이벤트 emit
const emit = defineEmits<{
  menuClick: [];
}>();

// 토큰에서 accountType 정보 가져오기 + 자료실 메뉴 이름 실시간 로드
onMounted(async () => {
  try {
    accountType.value = localStorage.getItem("accountType") ?? "";
  } catch (error) {
    console.error("토큰 파싱 실패:", error);
  }
  // admin 계정(gnbs 존재)일 때만 자료실 라벨 보정
  if (getAdminAuth()?.gnbs) {
    try {
      const rows = await lmsApi.get<Array<{ route: string; name: string }>>("/api/admin/material-menus");
      const map: Record<string, string> = {};
      for (const r of rows ?? []) map[r.route] = r.name;
      materialNames.value = map;
    } catch (e) {
      console.error("자료실 메뉴 이름 로드 실패:", e);
    }
  }
  // 미상담 가맹문의 배지: 최초 로드 + 주기 갱신(처리하면 사라지도록)
  if (isAgencyAcct.value) {
    loadFranchiseNew();
    franchiseNewTimer = setInterval(loadFranchiseNew, 60000);
  }
  // 게시판 New 배지: 최초 로드 + 게시판 열람(board-read) 시 갱신
  loadBoardUpdates();
  window.addEventListener("board-read", onBoardRead);
});

onUnmounted(() => {
  if (franchiseNewTimer) clearInterval(franchiseNewTimer);
  window.removeEventListener("board-read", onBoardRead);
});

function handleMenuClick(route?: string) {
  if (route) {
    router.push(route);
  }
  emit("menuClick");
}

function hasPermission(item: MenuItem) {
  if (!item.permission) return true;
  if (!accountType.value) return false;

  if (Array.isArray(item.permission)) {
    return item.permission.includes(accountType.value);
  }

  return item.permission === accountType.value;
}

const visibleMenu = computed(() => {
  return menuItems
    .filter(hasPermission) // 섹션 자체 권한(학습관리=원장·본사) 검사 — book(type 8)은 "book"이라 제외됨
    .map((section) => {
      const children = (section.children ?? []).filter(hasPermission);
      return {
        ...section,
        children,
      };
    })
    .filter((section) => (section.children?.length ?? 0) > 0);
});
</script>
